import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export default function Estatisticas() {
  const [semanas, setSemanas] = useState([])
  const [semanaId, setSemanaId] = useState(null)
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(true)
  const [timesFavoritos, setTimesFavoritos] = useState([])
  const [chartType, setChartType] = useState('bar')
  const [vidasType, setVidasType] = useState('bar')
  const [favsType, setFavsType] = useState('bar')
  const chartPicksRef = useRef(null)
  const chartVidasPerdidasRef = useRef(null)
  const chartSurvRef = useRef(null)
  const chartVidasRef = useRef(null)
  const chartFavsRef = useRef(null)
  const chartsRef = useRef({})

  // Carrega semanas processadas
  const { ligaAtual } = useAuth()
  const vidasMax = ligaAtual?.vidas_padrao || 3
  useEffect(() => {
    if (!ligaAtual?.id) return
    async function carregar() {
      const { data } = await supabase
        .from('semanas')
        .select('id, numero, tipo, picks_revelados, resultados_processados')
        .eq('liga_id', ligaAtual.id)
        .eq('picks_revelados', true)
        .order('numero', { ascending: true })
      setSemanas(data || [])
      if (data?.length) setSemanaId(data[data.length - 1].id)
      else setLoading(false)
    }
    carregar()
  }, [ligaAtual?.id])

  // Carrega dados da semana selecionada
  useEffect(() => {
    if (!semanaId) return
    async function carregar() {
      setLoading(true)

      // Times mais escolhidos
      const { data: picks } = await supabase
        .from('picks')
        .select('time_escolhido, semanas(picks_revelados)')
        .eq('semana_id', semanaId)
        .not('time_escolhido', 'is', null)

      // Sobreviventes por semana — filtrado pela liga atual via semanas
      const semanaIdsLiga = semanas.map(s => s.id)
      const { data: historico } = await supabase
        .from('historico_vidas')
        .select('semana_id, vidas, semanas(numero, liga_id)')
        .in('semana_id', semanaIdsLiga)
        .order('semana_id', { ascending: true })

      // Vidas perdidas por rodada — usa picks.vidas_antes/vidas_depois das semanas já processadas
      const semanaIdsProcessadas = semanas.filter(s => s.resultados_processados).map(s => s.id)
      const { data: picksVidas } = semanaIdsProcessadas.length
        ? await supabase
            .from('picks')
            .select('semana_id, vidas_antes, vidas_depois, semanas(numero)')
            .in('semana_id', semanaIdsProcessadas)
            .not('vidas_antes', 'is', null)
        : { data: [] }

      // Distribuição de vidas atual — usa ranking_liga para contornar RLS
      const { data: participantes } = await supabase
        .from('ranking_liga')
        .select('vidas_restantes')
        .eq('liga_id', ligaAtual?.id)

      // Processa times mais escolhidos
      const semanaAtual = semanas.find(s => s.id === semanaId)
      const revelado = semanaAtual?.picks_revelados
      let timesCount = {}
      if (revelado && picks) {
        picks.forEach(p => {
          if (p.time_escolhido) {
            timesCount[p.time_escolhido] = (timesCount[p.time_escolhido] || 0) + 1
          }
        })
      }
      const timesOrdenados = Object.entries(timesCount)
        .sort((a, b) => b[1] - a[1])

      // Processa sobreviventes por semana
      const sobreviventesPorSemana = {}
      if (historico) {
        historico.forEach(h => {
          const num = h.semanas?.numero
          if (!num) return
          if (!sobreviventesPorSemana[num]) sobreviventesPorSemana[num] = 0
          if (h.vidas > 0) sobreviventesPorSemana[num]++
        })
      }
      const survLabels = Object.keys(sobreviventesPorSemana).map(n => `S${n}`)
      const survData = Object.values(sobreviventesPorSemana)

      // Processa vidas perdidas por rodada
      const porRodada = {}
      if (picksVidas) {
        picksVidas.forEach(p => {
          const num = p.semanas?.numero
          if (!num) return
          if (!porRodada[num]) porRodada[num] = { perderam: 0, naoPerderam: 0 }
          if (p.vidas_depois < p.vidas_antes) porRodada[num].perderam++
          else porRodada[num].naoPerderam++
        })
      }
      const vidasPerdidas = Object.keys(porRodada)
        .map(Number)
        .sort((a, b) => a - b)
        .map(num => {
          const { perderam, naoPerderam } = porRodada[num]
          const total = perderam + naoPerderam
          return { numero: num, perderam, naoPerderam, total, pct: total ? Math.round((perderam / total) * 100) : 0 }
        })

      // Distribuição de vidas — dinâmico baseado em vidas_padrao da liga
      const dist = {}
      for (let i = 0; i <= vidasMax; i++) dist[i] = 0
      if (participantes) {
        participantes.forEach(p => {
          const v = Math.min(Math.max(p.vidas_restantes, 0), vidasMax)
          dist[v]++
        })
      }

      setDados({
        revelado,
        times: timesOrdenados,
        survLabels,
        survData,
        vidasPerdidas,
        dist,
        totalPicks: picks?.length || 0,
      })
      setLoading(false)
    }
    carregar()
  }, [semanaId, semanas])

  // Carrega times favoritos dos membros da liga via função SECURITY DEFINER
  useEffect(() => {
    if (!ligaAtual?.id) return
    async function carregarFavs() {
      const { data } = await supabase.rpc('times_favoritos_liga', { p_liga_id: ligaAtual.id })
      if (!data?.length) return
      setTimesFavoritos(data.map(r => [r.time_abbr, Number(r.quantidade)]))
    }
    carregarFavs()
  }, [ligaAtual?.id])

  // Renderiza gráficos via canvas manual
  useEffect(() => {
    if (!dados || loading) return
    drawPicksChart()
    drawVidasPerdidasChart()
    drawSurvChart()
    drawVidasChart()
  }, [dados, chartType, vidasType, loading])

  useEffect(() => {
    drawFavsChart()
  }, [timesFavoritos, favsType])

  function drawBar(ctx, x, y, w, h, color) {
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.roundRect(x, y, w, h, 4)
    ctx.fill()
  }

  function drawPicksChart() {
    const canvas = chartPicksRef.current
    if (!canvas || !dados) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)

    if (!dados.revelado) {
      ctx.fillStyle = '#999'
      ctx.font = '13px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Escolhas reveladas após início da rodada', W / 2, H / 2)
      return
    }
    if (!dados.times.length) {
      ctx.fillStyle = '#999'
      ctx.font = '13px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Nenhum pick registrado', W / 2, H / 2)
      return
    }

    const COLORS = ['#D85A30', '#378ADD', '#1D9E75', '#BA7517', '#534AB7', '#D4537E', '#5F5E5A', '#2C9E8F']
    const pad = { l: 10, r: 10, t: 30, b: 32 }
    const n = dados.times.length
    const total = dados.times.reduce((s, t) => s + t[1], 0)

    if (chartType === 'bar') {
      const barW = Math.floor((W - pad.l - pad.r - (n - 1) * 8) / n)
      const maxVal = Math.max(...dados.times.map(t => t[1]))
      const chartH = H - pad.t - pad.b

      dados.times.forEach(([time, val], i) => {
        const x = pad.l + i * (barW + 8)
        const bh = Math.round((val / maxVal) * chartH)
        const y = pad.t + chartH - bh
        drawBar(ctx, x, y, barW, bh, COLORS[i % COLORS.length])

        ctx.font = 'bold 11px sans-serif'
        ctx.textAlign = 'center'
        if (bh > 18) {
          // Valor dentro da barra
          ctx.fillStyle = 'white'
          ctx.fillText(val, x + barW / 2, y + bh / 2 + 4)
        } else {
          // Valor acima da barra
          ctx.fillStyle = '#444'
          ctx.fillText(val, x + barW / 2, y - 4)
        }

        // Label abaixo
        ctx.fillStyle = '#555'
        ctx.font = '11px sans-serif'
        ctx.fillText(time, x + barW / 2, H - 8)
      })
    } else {
      // Pizza: fatias grandes (≥8%) com label interno; pequenas em legenda embaixo
      const pequenas = dados.times.filter(([, v]) => Math.round((v / total) * 100) < 8)
      const legendRows = Math.ceil(pequenas.length / 3)
      const legendH = legendRows > 0 ? legendRows * 22 + 16 : 0
      const pieAreaH = 340
      canvas.height = pieAreaH + legendH
      ctx.clearRect(0, 0, W, canvas.height)

      const cx = W / 2
      const cy = pieAreaH / 2
      const r = Math.min(W / 2 - 20, pieAreaH / 2 - 20)
      let startAngle = -Math.PI / 2

      dados.times.forEach(([time, val], i) => {
        const slice = (val / total) * 2 * Math.PI
        const endAngle = startAngle + slice
        const mid = startAngle + slice / 2
        const pct = Math.round((val / total) * 100)

        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, r, startAngle, endAngle)
        ctx.closePath()
        ctx.fillStyle = COLORS[i % COLORS.length]
        ctx.fill()
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 2
        ctx.stroke()

        if (pct >= 8) {
          const lx = cx + Math.cos(mid) * r * 0.68
          const ly = cy + Math.sin(mid) * r * 0.68
          ctx.fillStyle = 'white'
          ctx.font = 'bold 11px sans-serif'
          ctx.textAlign = 'center'
          ctx.fillText(time, lx, ly - 7)
          ctx.fillText(val + ' · ' + pct + '%', lx, ly + 6)
        }
        startAngle = endAngle
      })

      // Legenda compacta (3 colunas) para fatias pequenas
      if (pequenas.length > 0) {
        const legY0 = pieAreaH + 8
        const colW = W / 3
        pequenas.forEach(([time, val], j) => {
          const globalIdx = dados.times.findIndex(([t]) => t === time)
          const row = Math.floor(j / 3)
          const col = j % 3
          const x = col * colW + 14
          const y = legY0 + row * 22
          const pct = Math.round((val / total) * 100)
          ctx.fillStyle = COLORS[globalIdx % COLORS.length]
          ctx.fillRect(x, y, 11, 11)
          ctx.fillStyle = '#444'
          ctx.font = '10px sans-serif'
          ctx.textAlign = 'left'
          ctx.fillText(`${time} · ${val} · ${pct}%`, x + 15, y + 9)
        })
      }
    }
  }

  function drawVidasPerdidasChart() {
    const canvas = chartVidasPerdidasRef.current
    if (!canvas || !dados) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)

    const arr = dados.vidasPerdidas || []
    if (!arr.length) {
      ctx.fillStyle = '#999'
      ctx.font = '13px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Nenhuma rodada processada ainda', W / 2, H / 2)
      return
    }

    const AZUL = '#378ADD', VERMELHO = '#E24B4A', LARANJA = '#D85A30'
    const n = arr.length
    const pad = { l: 10, r: 10 }
    const gap = 10
    const barW = Math.floor((W - pad.l - pad.r - (n - 1) * gap) / n)

    // Barras sempre nascem da mesma linha de base (eixo X)
    const baseline = H - 40
    const barsTop = 92
    const chartBarH = baseline - barsTop
    const maxTotal = Math.max(...arr.map(d => d.total), 1)
    const pxPerUnit = chartBarH / maxTotal

    // Linha de % numa faixa própria, acima do topo das barras
    const lineTop = 18
    const lineBottom = 82
    const maxPct = Math.max(...arr.map(d => d.pct), 1)
    const pctEscala = maxPct * 1.3
    const py = pct => lineBottom - (pct / pctEscala) * (lineBottom - lineTop)

    const pontosLinha = []

    arr.forEach((d, i) => {
      const x = pad.l + i * (barW + gap)
      const cx = x + barW / 2
      const blueH = d.naoPerderam * pxPerUnit
      const redH = d.perderam * pxPerUnit
      const blueY = baseline - blueH
      const redY = blueY - redH

      ctx.fillStyle = AZUL
      ctx.fillRect(x, blueY, barW, blueH)
      ctx.fillStyle = VERMELHO
      ctx.fillRect(x, redY, barW, redH)

      ctx.textAlign = 'center'
      ctx.font = 'bold 11px sans-serif'
      if (blueH > 16) {
        ctx.fillStyle = 'white'
        ctx.fillText(d.naoPerderam, cx, blueY + blueH / 2 + 4)
      }
      if (redH > 16) {
        ctx.fillStyle = 'white'
        ctx.fillText(d.perderam, cx, redY + redH / 2 + 4)
      } else if (d.perderam > 0) {
        ctx.fillStyle = '#444'
        ctx.font = '10px sans-serif'
        ctx.fillText(d.perderam, cx, redY - 4)
      }

      ctx.fillStyle = '#555'
      ctx.font = '11px sans-serif'
      ctx.fillText(`S${d.numero}`, cx, H - 24)

      pontosLinha.push([cx, py(d.pct)])
    })

    // Linha de percentual
    ctx.beginPath()
    pontosLinha.forEach(([x, y], i) => { i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y) })
    ctx.strokeStyle = LARANJA
    ctx.lineWidth = 2.5
    ctx.stroke()

    arr.forEach((d, i) => {
      const [x, y] = pontosLinha[i]
      ctx.beginPath()
      ctx.arc(x, y, 4, 0, Math.PI * 2)
      ctx.fillStyle = LARANJA
      ctx.fill()
      ctx.font = 'bold 10px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(`${d.pct}%`, x, y - 8)
    })

    // Legenda
    const legY = H - 8
    ctx.fillStyle = AZUL
    ctx.fillRect(W / 2 - 150, legY - 9, 10, 10)
    ctx.fillStyle = '#444'
    ctx.font = '10px sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText('não perderam vida', W / 2 - 136, legY)

    ctx.fillStyle = VERMELHO
    ctx.fillRect(W / 2 + 4, legY - 9, 10, 10)
    ctx.fillStyle = '#444'
    ctx.fillText('perderam vida', W / 2 + 18, legY)

    ctx.strokeStyle = LARANJA
    ctx.lineWidth = 2.5
    ctx.beginPath()
    ctx.moveTo(W / 2 + 110, legY - 4)
    ctx.lineTo(W / 2 + 128, legY - 4)
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(W / 2 + 119, legY - 4, 3, 0, Math.PI * 2)
    ctx.fillStyle = LARANJA
    ctx.fill()
    ctx.fillStyle = '#444'
    ctx.fillText('% que perderam', W / 2 + 133, legY)
  }

  function drawSurvChart() {
    const canvas = chartSurvRef.current
    if (!canvas || !dados?.survData.length) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)

    const pad = { l: 10, r: 10, t: 28, b: 28 }
    const n = dados.survLabels.length
    if (n < 2) {
      // Apenas 1 semana processada — exibe o valor de forma simples
      ctx.fillStyle = '#D85A30'
      ctx.font = 'bold 28px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(dados.survData[0], W / 2, H / 2 - 4)
      ctx.fillStyle = '#999'
      ctx.font = '12px sans-serif'
      ctx.fillText('sobreviventes — ' + dados.survLabels[0], W / 2, H / 2 + 18)
      return
    }
    const chartW = W - pad.l - pad.r
    const chartH = H - pad.t - pad.b
    const minVal = Math.min(...dados.survData) - 2
    const maxVal = Math.max(...dados.survData) + 2

    const px = i => pad.l + (i / (n - 1)) * chartW
    const py = v => pad.t + chartH - ((v - minVal) / (maxVal - minVal)) * chartH

    // Área
    ctx.beginPath()
    ctx.moveTo(px(0), py(dados.survData[0]))
    dados.survData.forEach((v, i) => { if (i > 0) ctx.lineTo(px(i), py(v)) })
    ctx.lineTo(px(n - 1), H - pad.b)
    ctx.lineTo(px(0), H - pad.b)
    ctx.closePath()
    ctx.fillStyle = 'rgba(216,90,48,0.08)'
    ctx.fill()

    // Linha
    ctx.beginPath()
    ctx.moveTo(px(0), py(dados.survData[0]))
    dados.survData.forEach((v, i) => { if (i > 0) ctx.lineTo(px(i), py(v)) })
    ctx.strokeStyle = '#D85A30'
    ctx.lineWidth = 2
    ctx.stroke()

    // Pontos + valores
    dados.survData.forEach((v, i) => {
      ctx.beginPath()
      ctx.arc(px(i), py(v), 4, 0, Math.PI * 2)
      ctx.fillStyle = '#D85A30'
      ctx.fill()

      ctx.fillStyle = '#D85A30'
      ctx.font = 'bold 11px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText(v, px(i), py(v) - 9)

      ctx.fillStyle = '#888'
      ctx.font = '11px sans-serif'
      ctx.fillText(dados.survLabels[i], px(i), H - 6)
    })
  }

  function drawVidasChart() {
    const canvas = chartVidasRef.current
    if (!canvas || !dados) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width, H = canvas.height
    ctx.clearRect(0, 0, W, H)

    const labels = []
    const values = []
    const colors = ['#1D9E75', '#BA7517', '#E24B4A', '#B4B2A9', '#534AB7', '#D85A30']
    for (let i = vidasMax; i >= 1; i--) {
      labels.push(i === 1 ? '1 vida' : `${i} vidas`)
      values.push(dados.dist[i] || 0)
    }
    labels.push('Eliminados')
    values.push(dados.dist[0] || 0)
    const total = values.reduce((s, v) => s + v, 0)

    if (vidasType === 'pie') {
      const cx = W / 2, cy = H / 2 - 5
      const r = Math.min(W, H) / 2 - 20
      let startAngle = -Math.PI / 2
      values.forEach((val, i) => {
        if (val === 0) return
        const slice = (val / total) * 2 * Math.PI
        const endAngle = startAngle + slice
        const mid = startAngle + slice / 2
        const pct = Math.round((val / total) * 100)
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, r, startAngle, endAngle)
        ctx.closePath()
        ctx.fillStyle = colors[i]
        ctx.fill()
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 2
        ctx.stroke()
        if (pct >= 12) {
          const lx = cx + Math.cos(mid) * r * 0.68
          const ly = cy + Math.sin(mid) * r * 0.68
          ctx.fillStyle = 'white'
          ctx.font = 'bold 11px sans-serif'
          ctx.textAlign = 'center'
          ctx.fillText(labels[i], lx, ly - 7)
          ctx.fillText(val + ' · ' + pct + '%', lx, ly + 6)
        } else if (pct >= 2) {
          const innerX = cx + Math.cos(mid) * r * 0.9
          const innerY = cy + Math.sin(mid) * r * 0.9
          const outerX = cx + Math.cos(mid) * (r + 16)
          const outerY = cy + Math.sin(mid) * (r + 16)
          const textX = cx + Math.cos(mid) * (r + 28)
          const textY = cy + Math.sin(mid) * (r + 28)
          ctx.beginPath()
          ctx.moveTo(innerX, innerY)
          ctx.lineTo(outerX, outerY)
          ctx.strokeStyle = '#999'
          ctx.lineWidth = 0.8
          ctx.stroke()
          ctx.fillStyle = '#333'
          ctx.font = 'bold 10px sans-serif'
          ctx.textAlign = Math.cos(mid) > 0 ? 'left' : 'right'
          ctx.fillText(labels[i] + ' ' + val, textX, textY - 3)
          ctx.font = '9px sans-serif'
          ctx.fillStyle = '#666'
          ctx.fillText(pct + '%', textX, textY + 7)
        }
        startAngle = endAngle
      })
    } else {
      const pad = { l: 10, r: 10, t: 30, b: 32 }
      const n = 4
      const barW = Math.floor((W - pad.l - pad.r - (n - 1) * 12) / n)
      const maxVal = Math.max(...values, 1)
      const chartH = H - pad.t - pad.b
      values.forEach((val, i) => {
        const x = pad.l + i * (barW + 12)
        const bh = Math.max(Math.round((val / maxVal) * chartH), val > 0 ? 20 : 0)
        const y = pad.t + chartH - bh
        drawBar(ctx, x, y, barW, bh, colors[i])
        if (bh > 18) {
          ctx.fillStyle = 'white'
          ctx.font = 'bold 12px sans-serif'
          ctx.textAlign = 'center'
          ctx.fillText(val, x + barW / 2, y + bh / 2 + 4)
        }
        ctx.fillStyle = '#555'
        ctx.font = '11px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(labels[i], x + barW / 2, H - 8)
      })
    }
  }

  function drawFavsChart() {
    const canvas = chartFavsRef.current
    if (!canvas || !timesFavoritos.length) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width

    const COLORS = ['#D85A30','#378ADD','#1D9E75','#BA7517','#534AB7','#D4537E','#2C9E8F','#5F5E5A','#E24B4A','#4A90D9',
                    '#C0392B','#27AE60','#8E44AD','#2980B9','#E67E22','#16A085','#D35400','#7F8C8D','#2ECC71','#E74C3C']
    const total = timesFavoritos.reduce((s, [, c]) => s + c, 0)

    // Fatias grandes (≥7%): label dentro · Fatias pequenas: legenda embaixo
    const pequenas = timesFavoritos.filter(([, v]) => Math.round((v / total) * 100) < 7)
    const legendRows = Math.ceil(pequenas.length / 3)
    const legendH = legendRows > 0 ? legendRows * 22 + 20 : 0
    const pieAreaH = 380
    const H = pieAreaH + legendH
    canvas.height = H
    ctx.clearRect(0, 0, W, H)
    const cx = W / 2
    const cy = pieAreaH / 2
    const r = Math.min(W / 2 - 16, pieAreaH / 2 - 16)

    let startAngle = -Math.PI / 2
    timesFavoritos.forEach(([time, val], i) => {
      const slice = (val / total) * 2 * Math.PI
      const endAngle = startAngle + slice
      const mid = startAngle + slice / 2
      const pct = Math.round((val / total) * 100)

      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, r, startAngle, endAngle)
      ctx.closePath()
      ctx.fillStyle = COLORS[i % COLORS.length]
      ctx.fill()
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 2
      ctx.stroke()

      if (pct >= 7) {
        const lx = cx + Math.cos(mid) * r * 0.65
        const ly = cy + Math.sin(mid) * r * 0.65
        ctx.fillStyle = 'white'
        ctx.font = 'bold 12px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(time, lx, ly - 7)
        ctx.fillText(val + ' · ' + pct + '%', lx, ly + 7)
      }
      startAngle = endAngle
    })

    // Legenda compacta (3 colunas) para fatias pequenas
    if (pequenas.length > 0) {
      const legY0 = pieAreaH + 10
      const cols = 3
      const colW = W / cols
      pequenas.forEach(([time, val], j) => {
        const globalIdx = timesFavoritos.findIndex(([t]) => t === time)
        const row = Math.floor(j / cols)
        const col = j % cols
        const x = col * colW + 14
        const y = legY0 + row * 22
        const pct = Math.round((val / total) * 100)

        ctx.fillStyle = COLORS[globalIdx % COLORS.length]
        ctx.fillRect(x, y, 11, 11)

        ctx.fillStyle = '#444'
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'left'
        ctx.fillText(`${time} · ${val} · ${pct}%`, x + 15, y + 9)
      })
    }
  }

  const semanaAtual = semanas.find(s => s.id === semanaId)

  return (
    <div>
      <div style={s.banner}>
        Estatísticas públicas · escolhas reveladas após início de cada rodada
      </div>

      {/* Seletor de semana + tipo de gráfico */}
      <div style={s.controls}>
        <select style={s.select} value={semanaId || ''} onChange={e => setSemanaId(Number(e.target.value))}>
          {semanas.map(s => (
            <option key={s.id} value={s.id}>
              Semana {s.numero} {s.id === semanas[semanas.length - 1]?.id ? '(mais recente)' : ''}
            </option>
          ))}
        </select>
        <div style={s.typeBtns}>
          <button style={{ ...s.typeBtn, ...(chartType === 'bar' ? s.typeBtnAtivo : {}) }} onClick={() => setChartType('bar')}>Barras</button>
          <button style={{ ...s.typeBtn, ...(chartType === 'pie' ? s.typeBtnAtivo : {}) }} onClick={() => setChartType('pie')}>Pizza</button>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>Carregando...</div>
      ) : (
        <>
          {/* Times mais escolhidos */}
          <div style={s.sectionLabel}>Times mais escolhidos — semana {semanaAtual?.numero}</div>
          {!semanaAtual?.picks_revelados ? (
            <div style={s.bloqueado}>🔒 Escolhas reveladas após o início da rodada</div>
          ) : (
            <canvas ref={chartPicksRef} width={620} height={260} style={s.canvas} aria-label="Times mais escolhidos" />
          )}

          {/* Vidas perdidas por rodada */}
          <div style={{ ...s.sectionLabel, marginTop: '1.4rem' }}>Quantidade de vidas perdidas por rodada</div>
          <canvas ref={chartVidasPerdidasRef} width={620} height={230} style={s.canvas} aria-label="Vidas perdidas por rodada" />

          {/* Sobreviventes */}
          <div style={{ ...s.sectionLabel, marginTop: '1.4rem' }}>Sobreviventes por semana</div>
          <canvas ref={chartSurvRef} width={620} height={160} style={s.canvas} aria-label="Sobreviventes por semana" />

          {/* Distribuição de vidas */}
          <div style={{ ...s.sectionLabel, marginTop: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Distribuição de vidas</span>
            <div style={s.typeBtns}>
              <button style={{ ...s.typeBtn, ...(vidasType === 'bar' ? s.typeBtnAtivo : {}) }} onClick={() => setVidasType('bar')}>Barras</button>
              <button style={{ ...s.typeBtn, ...(vidasType === 'pie' ? s.typeBtnAtivo : {}) }} onClick={() => setVidasType('pie')}>Pizza</button>
            </div>
          </div>
          <canvas ref={chartVidasRef} width={620} height={240} style={s.canvas} aria-label="Distribuição de vidas" />
        </>
      )}

      {/* Times favoritos dos participantes */}
      {timesFavoritos.length > 0 && (
        <>
          <div style={{ ...s.sectionLabel, marginTop: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Times que os participantes torcem</span>
            <div style={s.typeBtns}>
              <button style={{ ...s.typeBtn, ...(favsType === 'bar' ? s.typeBtnAtivo : {}) }} onClick={() => setFavsType('bar')}>Barras</button>
              <button style={{ ...s.typeBtn, ...(favsType === 'pie' ? s.typeBtnAtivo : {}) }} onClick={() => setFavsType('pie')}>Pizza</button>
            </div>
          </div>
          {favsType === 'bar' ? (
            <div style={s.favsCard}>
              {(() => {
                const totalFavs = timesFavoritos.reduce((sum, [, c]) => sum + c, 0)
                return timesFavoritos.map(([time, count], i) => (
                  <div key={time} style={s.favRow}>
                    <span style={s.favPos}>#{i + 1}</span>
                    <img src={`/logos/${time}.png`} alt={time} style={s.favLogo} onError={e => { e.target.style.display = 'none' }} />
                    <span style={s.favTime}>{time}</span>
                    <div style={s.favBarWrap}>
                      <div style={{ ...s.favBar, width: `${Math.round((count / timesFavoritos[0][1]) * 100)}%` }} />
                    </div>
                    <span style={s.favCount}>{count} {count === 1 ? 'fã' : 'fãs'}</span>
                    <span style={s.favPct}>{Math.round((count / totalFavs) * 100)}%</span>
                  </div>
                ))
              })()}
            </div>
          ) : (
            <canvas ref={chartFavsRef} width={620} height={560} style={s.canvas} aria-label="Times favoritos" />
          )}
        </>
      )}
    </div>
  )
}

const s = {
  banner: { background: '#EAF3F8', border: '0.5px solid #378ADD', borderRadius: 8, padding: '8px 12px', marginBottom: '.9rem', fontSize: 12, color: '#1A5C9A' },
  controls: { display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' },
  select: { fontSize: 12, padding: '6px 10px', borderRadius: 8, border: '0.5px solid #ddd', background: 'white', flex: 1, minWidth: 140 },
  typeBtns: { display: 'flex', gap: 4 },
  typeBtn: { padding: '6px 14px', fontSize: 12, border: '0.5px solid #ddd', borderRadius: 8, background: 'white', color: '#666', cursor: 'pointer' },
  typeBtnAtivo: { background: '#D85A30', color: 'white', border: '0.5px solid #D85A30' },
  sectionLabel: { fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 },
  canvas: { width: '100%', height: 'auto', display: 'block' },
  bloqueado: { background: '#f5f5f5', border: '0.5px solid #ddd', borderRadius: 8, padding: '1.5rem', textAlign: 'center', fontSize: 13, color: '#888' },
  loading: { fontSize: 13, color: '#888', padding: '2rem 0', textAlign: 'center' },
  favsCard: { background: 'white', border: '0.5px solid #eee', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 },
  favRow: { display: 'flex', alignItems: 'center', gap: 10 },
  favPos: { fontSize: 11, color: '#bbb', fontWeight: 600, minWidth: 26, textAlign: 'right' },
  favLogo: { width: 28, height: 28, objectFit: 'contain', flexShrink: 0 },
  favTime: { fontSize: 12, fontWeight: 600, color: '#0A2342', minWidth: 36 },
  favBarWrap: { flex: 1, background: '#f5f5f5', borderRadius: 99, height: 8, overflow: 'hidden' },
  favBar: { height: '100%', background: '#D85A30', borderRadius: 99, transition: 'width .4s' },
  favCount: { fontSize: 12, color: '#666', minWidth: 38, textAlign: 'right' },
  favPct: { fontSize: 11, color: '#aaa', minWidth: 32, textAlign: 'right' },
}
